import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, Settings, User, Mail, LogOut, Save, FileText, HardHat, Building2, UserCheck, ShieldCheck, Award } from "lucide-react";
import { toast } from "sonner";
import { adminGet, adminPut, getToken, setToken, clearToken } from "../lib/adminApi";
import { APPS } from "../data/apps";
import Logo from "../components/Logo";
import { ConstructionLeads, ConstructionArtisans, ConstructionProperties, ConstructionTenantRequests } from "./AdminConstruction";
import AdminUsers from "./AdminUsers";
import AdminRealizations from "./AdminRealizations";

const TABS = [
  { key: "stats", label: "Statistiques", icon: BarChart3, perm: "stats" },
  { key: "btp-leads", label: "Devis & demandes", icon: FileText, perm: "quotes" },
  { key: "btp-artisans", label: "Artisans", icon: HardHat, perm: "artisans" },
  { key: "btp-biens", label: "Biens & loyers", icon: Building2, perms: ["properties", "receipts"] },
  { key: "btp-realisations", label: "Réalisations", icon: Award, perm: "realizations" },
  { key: "btp-tenants", label: "Comptes locataires", icon: UserCheck, perm: "tenants" },
  { key: "settings", label: "Paramètres", icon: Settings, perm: "settings" },
  { key: "messages", label: "Messages", icon: Mail, perm: "settings" },
  { key: "users", label: "Utilisateurs & accès", icon: ShieldCheck, perm: "users" },
  { key: "account", label: "Compte", icon: User, superOnly: true },
];

export default function Admin() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [tab, setTab] = useState(null);
  const [email, setEmail] = useState("");
  const [perms, setPerms] = useState([]);
  const [isSuper, setIsSuper] = useState(false);
  const [pendingTenants, setPendingTenants] = useState(0);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    adminGet("/admin/me")
      .then((d) => {
        setEmail(d.email);
        setPerms(d.permissions || []);
        setIsSuper(!!d.is_super);
        setReady(true);
      })
      .catch(() => {
        clearToken();
        navigate("/admin/login");
      });
  }, [navigate]);

  const canSee = (t) => {
    if (isSuper) return true;
    if (t.superOnly) return false;
    const need = t.perms || [t.perm];
    return need.some((p) => perms.includes(p));
  };
  const visibleTabs = TABS.filter(canSee);

  useEffect(() => {
    if (!ready) return;
    if (!tab || !visibleTabs.some((t) => t.key === tab)) {
      setTab(visibleTabs[0]?.key || null);
    }
  }, [ready, perms, isSuper]); // eslint-disable-line

  useEffect(() => {
    if (!ready) return;
    if (!isSuper && !perms.includes("tenants")) return;
    adminGet("/admin/tenant-requests")
      .then((d) => setPendingTenants(d.filter((x) => x.status === "pending").length))
      .catch(() => {});
  }, [ready, tab, isSuper, perms]);

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  if (!ready) return <div className="min-h-screen bg-navy" />;

  const isTestEnv = typeof window !== "undefined" && !window.location.hostname.endsWith("tireragroup.com");

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-navy text-white">
      {isTestEnv && (
        <div data-testid="test-env-banner" className="bg-amber-500 px-4 py-2.5 text-center text-sm font-semibold text-black">
          ⚠️ SITE DE TEST — vos modifications ici n'apparaissent PAS sur votre site en ligne. Pour gérer vos vraies données, allez sur{" "}
          <a href="https://tireragroup.com/admin" className="underline">tireragroup.com/admin</a>.
        </div>
      )}
      <header className="border-b border-white/10">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Logo className="h-8 w-8" light />
            <span className="font-display font-bold">Administration</span>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-sm text-white/70 hover:text-white">
            <LogOut size={16} /> Déconnexion
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-8 grid gap-8 lg:grid-cols-[220px_1fr]">
        <nav className="flex lg:flex-col gap-2 overflow-x-auto">
          {visibleTabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                  tab === t.key ? "bg-white text-navy" : "text-white/70 hover:bg-white/5"
                }`}
              >
                <Icon size={17} /> {t.label}
                {t.key === "btp-tenants" && pendingTenants > 0 && (
                  <span data-testid="tenant-pending-badge" className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-xs font-bold text-black">
                    {pendingTenants}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div>
          {tab === "stats" && <StatsEditor />}
          {tab === "btp-leads" && <ConstructionLeads />}
          {tab === "btp-artisans" && <ConstructionArtisans />}
          {tab === "btp-biens" && <ConstructionProperties />}
          {tab === "btp-realisations" && <AdminRealizations />}
          {tab === "btp-tenants" && <ConstructionTenantRequests />}
          {tab === "settings" && <SettingsEditor />}
          {tab === "account" && <AccountEditor email={email} onEmailChange={setEmail} />}
          {tab === "messages" && <Messages />}
          {tab === "users" && <AdminUsers />}
        </div>
      </div>
    </main>
  );
}

function Card({ title, desc, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-8">
      <h2 className="font-display font-semibold text-xl">{title}</h2>
      {desc && <p className="mt-1 text-sm text-white/60">{desc}</p>}
      <div className="mt-6">{children}</div>
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-white placeholder-white/40 outline-none focus:border-white/40";

function StatsEditor() {
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGet("/admin/stats").then((d) => setValues(d.values)).catch(() => toast.error("Erreur de chargement"));
  }, []);

  const update = (slug, key, v) => setValues((s) => ({ ...s, [slug]: { ...s[slug], [key]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      for (const slug of Object.keys(values)) {
        clean[slug] = {};
        for (const k of Object.keys(values[slug])) clean[slug][k] = Number(values[slug][k]) || 0;
      }
      await adminPut("/admin/stats", { values: clean });
      toast.success("Statistiques mises à jour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  if (!values) return <Card title="Statistiques"><p className="text-white/50">Chargement…</p></Card>;

  return (
    <Card title="Statistiques" desc="Modifiez les chiffres de base de chaque application. Le site affiche automatiquement les valeurs arrondies (« Plus de » / « Près de »).">
      <div className="space-y-8">
        {APPS.filter((a) => a.stats).map((a) => (
          <div key={a.slug}>
            <div className="flex items-center gap-2 mb-3">
              <span className="h-3 w-3 rounded-full" style={{ backgroundColor: a.accent }} />
              <span className="font-display font-semibold">{a.name}</span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {a.stats.map((s) => (
                <label key={s.key} className="block">
                  <span className="mb-1.5 block text-xs text-white/60">{s.label}</span>
                  <input
                    type="number"
                    data-testid={`admin-stat-${a.slug}-${s.key}`}
                    value={values[a.slug]?.[s.key] ?? 0}
                    onChange={(e) => update(a.slug, s.key, e.target.value)}
                    className={inputCls}
                  />
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} data-testid="admin-save-stats" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-navy hover:bg-white/90 disabled:opacity-60">
        <Save size={16} /> {saving ? "Enregistrement…" : "Enregistrer"}
      </button>
    </Card>
  );
}

function SettingsEditor() {
  const [url, setUrl] = useState("");
  const [notifEmail, setNotifEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGet("/admin/settings").then((d) => {
      setUrl(d.privacy_url || "");
      setNotifEmail(d.notification_email || "");
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminPut("/admin/settings", { privacy_url: url, notification_email: notifEmail });
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Paramètres du site" desc="Adresse de réception des notifications et lien de politique de confidentialité.">
      <label className="block">
        <span className="mb-1.5 block text-sm text-white/70">E-mail de réception des notifications</span>
        <input
          value={notifEmail}
          onChange={(e) => setNotifEmail(e.target.value)}
          data-testid="admin-notification-email"
          type="email"
          className={inputCls}
          placeholder="tireragroup@gmail.com"
        />
        <span className="mt-1.5 block text-xs text-white/40">
          Toutes les alertes (devis, artisans, reçus, comptes locataires) seront envoyées à cette adresse. Laissez vide pour utiliser l'e-mail de votre compte admin. N'affecte pas votre connexion.
        </span>
      </label>
      <label className="mt-5 block">
        <span className="mb-1.5 block text-sm text-white/70">URL Politique de confidentialité</span>
        <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="admin-privacy-url" className={inputCls} placeholder="https://…" />
      </label>
      <button onClick={save} disabled={saving} data-testid="admin-save-settings" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-navy hover:bg-white/90 disabled:opacity-60">
        <Save size={16} /> {saving ? "Enregistrement…" : "Enregistrer"}
      </button>
    </Card>
  );
}

function AccountEditor({ email, onEmailChange }) {
  const [newEmail, setNewEmail] = useState(email);
  const [current, setCurrent] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => setNewEmail(email), [email]);

  const save = async () => {
    if (!current) return toast.error("Saisissez votre mot de passe actuel");
    setSaving(true);
    try {
      const body = { current_password: current };
      if (newEmail && newEmail !== email) body.new_email = newEmail;
      if (newPassword) body.new_password = newPassword;
      const d = await adminPut("/admin/credentials", body);
      setToken(d.token);
      onEmailChange(d.email);
      setNewEmail(d.email);
      setCurrent("");
      setNewPassword("");
      toast.success("Identifiants mis à jour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Compte administrateur" desc="Modifiez votre adresse email et votre mot de passe.">
      <div className="space-y-4 max-w-md">
        <label className="block">
          <span className="mb-1.5 block text-sm text-white/70">Adresse email</span>
          <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="admin-new-email" className={inputCls} />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm text-white/70">Nouveau mot de passe (laisser vide pour ne pas changer)</span>
          <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="admin-new-password" className={inputCls} placeholder="••••••••" />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm text-white/70">Mot de passe actuel (obligatoire pour confirmer)</span>
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="admin-current-password" className={inputCls} placeholder="••••••••" />
        </label>
      </div>
      <button onClick={save} disabled={saving} data-testid="admin-save-account" className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-navy hover:bg-white/90 disabled:opacity-60">
        <Save size={16} /> {saving ? "Enregistrement…" : "Enregistrer"}
      </button>
    </Card>
  );
}

function Messages() {
  const [msgs, setMsgs] = useState(null);
  useEffect(() => {
    adminGet("/admin/messages").then(setMsgs).catch(() => toast.error("Erreur de chargement"));
  }, []);

  return (
    <Card title="Messages de contact" desc="Messages reçus via le formulaire de contact.">
      {!msgs ? (
        <p className="text-white/50">Chargement…</p>
      ) : msgs.length === 0 ? (
        <p className="text-white/50" data-testid="admin-no-messages">Aucun message pour le moment.</p>
      ) : (
        <div className="space-y-3">
          {msgs.map((m, i) => (
            <div key={i} data-testid="admin-message-item" className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium">{m.name} <span className="text-white/50">· {m.email}</span></span>
                <span className="text-xs text-white/40">{new Date(m.created_at).toLocaleString("fr-FR")}</span>
              </div>
              {m.subject && <div className="mt-1 text-sm font-medium text-white/80">{m.subject}</div>}
              <p className="mt-2 text-sm text-white/70 whitespace-pre-wrap">{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
